import { FC, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { FolderSpecial, HideSource, Info } from '@mui/icons-material'
import { Button, ButtonGroup, FormControl, Grid, Typography } from '@mui/material'

import DateRangePicker, { DateRangeValue } from '../../../../components/DateRangePicker/DateRangePicker'
import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { SelectMenu } from '../../../../components/SelectMenu/SelectMenu'
import { dateRangeOption30Days, dateRangeOption60Days, dateRangeOption7Days } from '../../../../types/DateRangeOptions'
import { RoleEnum } from '../../../account'
import { useRoleCheck } from '../../../account/hooks/roleHooks'
import { useInternalAccessCheck } from '../../../account/hooks/roleHooks'
import { usePerformanceChangesByOptions } from '../../hooks/usePerformanceChangesByOptions'
import { SortGamesByOptionValue, useSortGamesBySelectOptions } from '../../hooks/useSortGamesBySelectOptions'
import { useThresholdOptions } from '../../hooks/useThresholdOptions'
import { LiveEventTrackerTab } from '../../types/LiveEventTrackerTab'
import { PerformanceChangesByOptionValue } from '../../types/PerformanceChangesByOption'

type LiveEventsFiltersProps = {
  dateRange?: DateRangeValue
  minDate?: Date
  maxDate?: Date
  sortGamesBy: SortGamesByOptionValue
  threshold: number
  performanceChangesBy: PerformanceChangesByOptionValue
  groupByArchetype: boolean
  hideDiscontinuedArchetypes: boolean
  onDateRangeChange: (value?: DateRangeValue) => void
  onSortGamesByChange?: (value: SortGamesByOptionValue) => void
  onThresholdChange: (value: number) => void
  onPerformanceChangesByChange: (value: PerformanceChangesByOptionValue) => void
  onGroupByArchetypeChanged: () => void
  onHideDiscontinuedArchetypesChanged: () => void
  config: LiveEventsFiltersConfig
}

export type LiveEventsFiltersConfig = {
  performanceChangesByDisabled?: boolean
  thresholdDisabled?: boolean
  sortGamesByDisabled?: boolean
}

export const LiveEventsFilters: FC<LiveEventsFiltersProps> = ({
  dateRange,
  minDate,
  maxDate,
  sortGamesBy,
  threshold,
  performanceChangesBy,
  groupByArchetype,
  hideDiscontinuedArchetypes,
  onDateRangeChange,
  onSortGamesByChange,
  onThresholdChange,
  onPerformanceChangesByChange,
  onGroupByArchetypeChanged,
  onHideDiscontinuedArchetypesChanged,
  config,
}) => {
  const { t } = useTranslation()
  const { subpage } = useParams<{ subpage: string }>()
  const liveEventsTrackerUnlocked = useRoleCheck(RoleEnum.live_events_tracker)
  const isInternalUser = useInternalAccessCheck()
  const sortGamesByOptions = useSortGamesBySelectOptions()
  const thresholdOptions = useThresholdOptions()
  const performanceChangesByOptions = usePerformanceChangesByOptions()
  const dateRangeOptions = useMemo(() => {
    return [dateRangeOption7Days, dateRangeOption30Days, dateRangeOption60Days]
  }, [])

  return (
    <Grid container alignItems="center" justifyContent="space-between" wrap="wrap" spacing={2}>
      <Grid item>
        <DateRangePicker
          value={dateRange}
          onChangeValue={(value) => onDateRangeChange(value)}
          minDate={minDate}
          maxDate={maxDate}
          disabled={!liveEventsTrackerUnlocked}
          dateRangeOptions={dateRangeOptions}
        />
      </Grid>

      {/* TODO: Remove internal user check when we want to release group by archetype to all users */}
      {(subpage === LiveEventTrackerTab.Games || subpage === 'live-events') && isInternalUser && (
        <Grid item>
          <FormControl size="small" variant="outlined">
            <ButtonGroup>
              <GRTooltip content={t(!groupByArchetype ? 'live-events:group_by_archetype' : 'live-events:ungroup_by_archetype')}>
                <Button onClick={() => onGroupByArchetypeChanged()} variant={groupByArchetype ? 'contained' : 'outlined'}>
                  <FolderSpecial />
                </Button>
              </GRTooltip>

              <GRTooltip content={t(hideDiscontinuedArchetypes ? 'live-events:show_discontinued_archetypes' : 'live-events:hide_discontinued_archetypes')}>
                <Button
                  onClick={() => onHideDiscontinuedArchetypesChanged()}
                  disabled={!groupByArchetype}
                  variant={hideDiscontinuedArchetypes ? 'contained' : 'outlined'}
                >
                  <HideSource />
                </Button>
              </GRTooltip>
            </ButtonGroup>
          </FormControl>
        </Grid>
      )}

      <Grid item>
        <Grid container alignItems="center" wrap="wrap" spacing={2}>
          <Grid item>
            <Grid container alignItems="center" gap={1}>
              <FormControl size="small" variant="outlined" sx={{ minWidth: 200 }}>
                <SelectMenu
                  label={t('live-events:performance_changes_by_label')}
                  value={performanceChangesBy}
                  onChange={(event) => onPerformanceChangesByChange(event.target.value)}
                  options={performanceChangesByOptions}
                  disabled={config.performanceChangesByDisabled}
                />
              </FormControl>
              <GRTooltip
                content={
                  <Typography variant="body1" component="div">
                    <Trans i18nKey={'live-events:performance_changes_by_tooltip'} />
                  </Typography>
                }
                hidden={config.performanceChangesByDisabled}
              >
                <Info color="primary" sx={{ color: (theme) => (config.performanceChangesByDisabled ? theme.palette.grey[400] : 'primary') }} />
              </GRTooltip>
            </Grid>
          </Grid>
          <Grid item>
            <FormControl size="small" variant="outlined" sx={{ minWidth: 100 }}>
              <SelectMenu
                label={t('live-events:threshold_label')}
                value={threshold}
                onChange={(event) => onThresholdChange(event.target.value)}
                options={thresholdOptions}
                disabled={config.thresholdDisabled}
              />
            </FormControl>
          </Grid>
          {onSortGamesByChange && (
            <Grid item>
              <FormControl size="small" variant="outlined" sx={{ minWidth: 100 }}>
                <SelectMenu
                  label={t('live-events:sort_games_by_label')}
                  value={sortGamesBy}
                  onChange={(event) => onSortGamesByChange(event.target.value)}
                  options={sortGamesByOptions}
                  disabled={config.sortGamesByDisabled}
                />
              </FormControl>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  )
}
